import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import InquiryNotes from "@/components/InquiryNotes";

export const INQUIRY_STATUS = {
  new: { label: "Nouvelle", cls: "bg-[#E0C173]/20 text-[#8a6d22] border-[#E0C173]" },
  in_progress: { label: "En cours", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  done: { label: "Traitée", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const FILTERS = [["all", "Toutes"], ["new", "Nouvelles"], ["in_progress", "En cours"], ["done", "Traitées"]];

const InquiryItem = ({ q, onChange }) => {
  const setStatus = async (status) => {
    try {
      await api.patch(`/admin/inquiries/${q.inquiry_id}/status`, { status });
      toast.success(`Demande marquée « ${INQUIRY_STATUS[status].label} »`);
      onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid={`inquiry-${q.inquiry_id}`} className={`bg-white border p-5 ${q.status === "new" ? "border-l-4 border-l-[#E0C173] border-gray-200" : "border-gray-200"}`}>
      <div className="flex justify-between flex-wrap gap-2">
        <div className="font-medium text-[#1B2A4A]">{q.user_name} · <a href={`mailto:${q.user_email}`} className="text-[#475569] font-normal hover:text-[#1B2A4A] inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{q.user_email}</a></div>
        <div className="text-xs text-gray-400">{new Date(q.created_at).toLocaleString("fr-FR")}</div>
      </div>
      <div className="text-xs text-[#C9A85A] uppercase tracking-wider mt-1">{q.business_title}</div>
      <p className="mt-2 text-sm text-[#475569] whitespace-pre-line">{q.message}</p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wider text-gray-500 mr-1">Statut</span>
        {Object.entries(INQUIRY_STATUS).map(([key, s]) => (
          <button
            key={key}
            data-testid={`inquiry-status-${key}-${q.inquiry_id}`}
            onClick={() => q.status !== key && setStatus(key)}
            className={`text-xs px-3 py-1 border transition-colors ${q.status === key ? s.cls + " font-semibold" : "border-gray-300 text-gray-600 hover:text-[#1B2A4A] hover:border-gray-400"}`}
          >
            {s.label}
          </button>
        ))}
      </div>
      <InquiryNotes q={q} onChange={onChange} />
    </div>
  );
};

export default function InquiriesPanel({ inquiries, onChange }) {
  const [filter, setFilter] = useState("all");
  const count = (k) => (k === "all" ? inquiries.length : inquiries.filter((q) => q.status === k).length);
  const shown = filter === "all" ? inquiries : inquiries.filter((q) => q.status === filter);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([k, label]) => (
          <button key={k} data-testid={`inquiry-filter-${k}`} onClick={() => setFilter(k)}
            className={`text-sm px-4 py-1.5 border transition-colors ${filter === k ? "bg-[#1B2A4A] text-white border-[#1B2A4A]" : "bg-white border-gray-200 text-[#475569] hover:border-[#1B2A4A]"}`}>
            {label} ({count(k)})
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {shown.length === 0 && <p data-testid="inquiries-empty" className="text-gray-400 py-10 text-center">Aucune demande dans cette catégorie.</p>}
        {shown.map((q) => <InquiryItem key={q.inquiry_id} q={q} onChange={onChange} />)}
      </div>
    </div>
  );
}
